import { useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db700kmcdv04m4p4ge6g";

type Status = "idle" | "submitting" | "success" | "error";

export default function FeedbackForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [message, setMessage] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const code = parsed?.code;
      const isSpam =
        typeof serverMsg === "string" &&
        serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && code === "OK" && !isSpam;

      if (isOk) {
        setStatus("success");
        setMessage("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg.trim()
            ? serverMsg
            : "提交失败，请稍后重试",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试");
    }
  };

  return (
    <form
      data-readdy-form
      id="feedback-form"
      onSubmit={handleSubmit}
      className="p-5 md:p-7 rounded-lg bg-background-50 border border-background-200"
    >
      <input
        type="text"
        name="website_alt"
        className="form-hp-cover"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label
            htmlFor="feedback-name"
            className="block mb-2 text-sm font-medium text-foreground-800"
          >
            姓名
          </label>
          <input
            id="feedback-name"
            type="text"
            name="name"
            required
            placeholder="请输入您的称呼"
            className="w-full px-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50"
          />
        </div>
        <div>
          <label
            htmlFor="feedback-email"
            className="block mb-2 text-sm font-medium text-foreground-800"
          >
            邮箱
          </label>
          <input
            id="feedback-email"
            type="email"
            name="email"
            required
            placeholder="you@example.com"
            className="w-full px-4 py-2.5 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50"
          />
        </div>
      </div>

      <div className="mt-4">
        <label
          htmlFor="feedback-message"
          className="block mb-2 text-sm font-medium text-foreground-800"
        >
          留言
        </label>
        <textarea
          id="feedback-message"
          name="message"
          required
          maxLength={500}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          placeholder="想看的视频类型、内容建议或使用反馈，欢迎告诉我们…"
          className="w-full px-4 py-3 text-sm rounded-md bg-background-100 border border-background-200 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 resize-none"
        ></textarea>
        <p className="mt-1.5 text-right text-xs text-foreground-400">
          {message.length} / 500
        </p>
      </div>

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 dark:text-foreground-950 font-semibold whitespace-nowrap hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin"></i>
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-line"></i>
            提交反馈
          </>
        )}
      </button>

      {status === "success" && (
        <p className="mt-4 flex items-center gap-2 text-sm text-primary-700">
          <i className="ri-checkbox-circle-fill text-lg"></i>
          提交成功，感谢您的反馈！我们会尽快查看。
        </p>
      )}
      {status === "error" && (
        <p className="mt-4 flex items-center gap-2 text-sm text-accent-700">
          <i className="ri-error-warning-fill text-lg"></i>
          {formError}
        </p>
      )}
    </form>
  );
}